export class RailsApiWrapper {
    constructor(config = {}) {
        this.baseURL = '';
        this.apiToken = '';
        this.offlineMode = false;
        this.id = Math.random().toString(36).substring(2, 15);
        this.config = config;
        this.baseURL = config.baseURL || '';
        this.offlineMode = config.offlineMode || false;
    }
    // Set the offlineMode flag
    setOfflineMode(offlineMode) {
        this.offlineMode = !!offlineMode;
    }
    // Get the offlineMode flag
    getOfflineMode() {
        return this.offlineMode;
    }
    // Dynamically get CSRF token from meta tag
    get csrfToken() {
        if (typeof document !== 'undefined') {
            const csrfMeta = document.querySelector('meta[name="csrf-token"]');
            return csrfMeta ? csrfMeta.content : '';
        }
        return '';
    }
    // Set the Rails API base URL (empty string means relative URLs)
    setBaseUrl(url) {
        // Normalize: remove trailing '/api' or '/api/'
        const normalized = url?.trim().replace(/\/api\/?$/, '') || '';
        this.baseURL = normalized;
        this.config.baseURL = this.baseURL; // Keep config in sync
    }
    // Get the Rails API base URL (empty string means relative URLs)
    getBaseUrl() {
        return this.baseURL;
    }
    // Set the API token
    setApiToken(token) {
        this.apiToken = token?.trim() || '';
    }
    // Get the API token
    getApiToken() {
        return this.apiToken;
    }
    // DRY: Helper to construct full URL
    getUrl(path) {
        const base = this.config.baseURL || this.baseURL;
        if (!base) {
            return path; // Relative URL if no base
        }
        // Ensure base ends with / and path doesn't start with /
        const normalizedBase = base.endsWith('/') ? base : `${base}/`;
        const normalizedPath = path.startsWith('/') ? path.substring(1) : path;
        return `${normalizedBase}${normalizedPath}`;
    }
    // Add executeWithRetry for Rails API calls
    async executeWithRetry(operation, maxRetries = RailsApiWrapper.MAX_RETRIES, initialDelay = RailsApiWrapper.INITIAL_DELAY) {
        let retries = 0;
        const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
        while (retries < maxRetries) {
            try {
                const result = await operation();
                return result;
            }
            catch (error) {
                retries++;
                if (retries < maxRetries) {
                    await delay(initialDelay * retries);
                }
                else {
                    throw error;
                }
            }
        }
        throw new Error('executeWithRetry: Should not reach here');
    }
    // DRY: Generic fetch method
    async fetch(path, options = {}) {
        return this.executeWithRetry(async () => {
            const offlineMode = this.config.offlineMode ?? this.offlineMode;
            if (offlineMode) {
                // Return a resolved promise with empty or mock data
                return Promise.resolve({ offlineMode: true });
            }
            const url = this.getUrl(path);
            // Add CSRF token to headers for non-GET requests
            const method = options.method || 'GET';
            const headers = {
                ...(options.headers || {}),
            };
            if (method !== 'GET') {
                const csrfToken = this.csrfToken;
                if (csrfToken) {
                    headers['X-CSRF-Token'] = csrfToken;
                }
            }
            // Add Authorization header for API requests that match patterns
            const apiRoutePatterns = this.config.apiRoutePatterns || [];
            const token = this.apiToken;
            const matchesPattern = apiRoutePatterns.some((pattern) => path.includes(pattern));
            if (matchesPattern && token) {
                headers['Authorization'] = `Bearer ${token}`;
            }
            options.headers = headers;
            const response = await fetch(url, options);
            const text = await response.text();
            if (!response.ok) {
                throw new Error(`HTTP error ${response.status}: ${text}`);
            }
            try {
                return JSON.parse(text);
            }
            catch {
                return text;
            }
        });
    }
    // Get customer info
    async getCustomerInfo() {
        const offlineMode = this.config.offlineMode ?? this.offlineMode;
        if (offlineMode)
            return Promise.resolve({ offlineMode: true });
        const endpoint = this.config.endpoints?.customerInfo || '/customer_info';
        return this.fetch(endpoint);
    }
    // Generate OpenAI key
    async generateOpenAiKey(feature) {
        const offlineMode = this.config.offlineMode ?? this.offlineMode;
        if (offlineMode)
            return Promise.resolve({ offlineMode: true });
        const endpoint = this.config.endpoints?.generateOpenAiKey || '/api/ai_proxy/generate_key';
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
        };
        if (feature) {
            requestOptions.body = JSON.stringify({ feature });
        }
        return this.fetch(endpoint, requestOptions);
    }
    /**
     * Update a single setting for a given user/profile using kwargs (e.g., survey_clicked: true, profileId, userId)
     * @param kwargs e.g., { survey_clicked: true, profileId, userId }
     */
    async updateSetting(kwargs) {
        const offlineMode = this.config.offlineMode ?? this.offlineMode;
        if (offlineMode)
            return Promise.resolve({ offlineMode: true });
        if (!this.config.endpoints?.settings) {
            throw new Error('Settings endpoint is not configured');
        }
        // Extract known keys
        const { profileId, userId, ...settings } = kwargs;
        const settingEntries = Object.entries(settings).filter(([k]) => k !== 'profileId' && k !== 'userId');
        if (settingEntries.length !== 1) {
            throw new Error('updateSetting expects exactly one setting key');
        }
        const [settingType, value] = settingEntries[0];
        const body = {
            value,
        };
        if (profileId)
            body.profile_id = profileId;
        if (userId)
            body.user_id = userId;
        const endpoint = `${this.config.endpoints.settings}/${encodeURIComponent(settingType)}`;
        return this.fetch(endpoint, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
    }
    /**
     * Get a setting for a given type and user/profile
     * @param settingType The type/key of the setting (e.g., 'survey_clicked')
     * @param options Optional object: { profileId?: string, userId?: string }
     * @returns Promise resolving to the setting value object
     */
    async getSetting(settingType, options = {}) {
        const offlineMode = this.config.offlineMode ?? this.offlineMode;
        if (offlineMode)
            return Promise.resolve({ offlineMode: true });
        if (!this.config.endpoints?.settings) {
            throw new Error('Settings endpoint is not configured');
        }
        const { profileId, userId } = options;
        const params = new URLSearchParams();
        if (profileId)
            params.append('profile_id', profileId);
        if (userId)
            params.append('user_id', userId);
        const query = params.toString() ? `?${params.toString()}` : '';
        const endpoint = `${this.config.endpoints.settings}/${encodeURIComponent(settingType)}${query}`;
        return this.fetch(endpoint);
    }
    /**
     * Delete a setting for a given type and user/profile
     * @param params { settingType: string, profileId?: string, userId?: string }
     * @returns Promise resolving to the delete result
     */
    async deleteSetting({ settingType, profileId, userId, }) {
        const offlineMode = this.config.offlineMode ?? this.offlineMode;
        if (offlineMode)
            return Promise.resolve({ offlineMode: true });
        if (!this.config.endpoints?.settings) {
            throw new Error('Settings endpoint is not configured');
        }
        const params = new URLSearchParams();
        if (profileId)
            params.append('profile_id', profileId);
        if (userId)
            params.append('user_id', userId);
        const query = params.toString() ? `?${params.toString()}` : '';
        const endpoint = `${this.config.endpoints.settings}/${encodeURIComponent(settingType)}${query}`;
        return this.fetch(endpoint, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
        });
    }
}
RailsApiWrapper.MAX_RETRIES = 5;
RailsApiWrapper.INITIAL_DELAY = 1000;
